import { ActionIcon, Alert, Box, Button, CopyButton, Collapse, Drawer, Flex, Group, Skeleton, Stack, Text, Tooltip } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  ArrowClockwiseIcon,
  CaretDownIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CheckIcon,
  CopyIcon,
  WarningIcon,
  XIcon,
} from "@phosphor-icons/react";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import useSwr from "swr";
import { LedgerApi } from "../../apis";
import { IInboxEventDetail } from "../../apis/ledger";
import { STATUS_PILL } from "./events-shared";
import eventClasses from "./events.module.css";
import { EntryLinesCard } from "./JournalEntryDrawer";
import { useTemplateLabel } from "./journal-shared";
import classes from "./journal.module.css";

type Props = {
  eventId: string | null;
  orderedIds: string[];
  canReplay: boolean;
  suspendKeys?: boolean;
  onClose: () => void;
  onNavigate: (id: string) => void;
  onRequestReplay: (detail: IInboxEventDetail) => void;
  /** Replay-ийн дараа дахин уншуулахад ашиглана. */
  refreshToken: number;
};

function isTyping(el: Element | null) {
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || (el as HTMLElement).isContentEditable;
}

export const useEventName = () => {
  const { t } = useTranslation("billing");
  return (type: string) => t(`ledger.inbox.event.${type}`, { defaultValue: type });
};

export const useAggregateName = () => {
  const { t } = useTranslation(["billing", "common"]);
  return (type: string | null) =>
    type ? t(`billing:ledger.inbox.aggregate.${type}`, { defaultValue: type }) : t("common:list.dash");
};

/**
 * EventDrawer
 * ├── header (нэр · төлөв · ‹ › · хаах)
 * ├── dl (эх сурвалж · загвар · хүлээж авсан · оролдлого)
 * ├── төлвөөс хамаарах хэсэг (PENDING_COST / FAILED / SKIPPED / POSTED бичилт)
 * ├── Payload (Collapse)
 * └── footer (Хаах · Дахин оролдох)
 */
export function EventDrawer({
  eventId,
  orderedIds,
  canReplay,
  suspendKeys,
  onClose,
  onNavigate,
  onRequestReplay,
  refreshToken,
}: Props) {
  const { t } = useTranslation(["billing", "common"]);
  const isNarrow = useMediaQuery("(max-width: 48em)");
  const eventName = useEventName();
  const aggregateName = useAggregateName();
  const templateLabel = useTemplateLabel();
  const [payloadOpen, setPayloadOpen] = useState(false);
  const opened = eventId !== null;

  const { data, error, isLoading, mutate } = useSwr<IInboxEventDetail>(
    eventId ? `ledger.inbox.detail.${eventId}` : null,
    async () => await LedgerApi.getInboxEvent(eventId!),
    { revalidateOnFocus: false },
  );

  useEffect(() => {
    if (refreshToken > 0) mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  useEffect(() => setPayloadOpen(false), [eventId]);

  const detail = data && eventId && data.id === eventId ? data : null;
  const index = eventId ? orderedIds.indexOf(eventId) : -1;
  const prevId = index > 0 ? orderedIds[index - 1] : null;
  const nextId = index >= 0 && index < orderedIds.length - 1 ? orderedIds[index + 1] : null;

  useEffect(() => {
    if (!opened || suspendKeys) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.altKey || ev.ctrlKey || ev.metaKey || isTyping(document.activeElement)) return;
      if (ev.key === "ArrowLeft" && prevId) {
        ev.preventDefault();
        onNavigate(prevId);
      } else if (ev.key === "ArrowRight" && nextId) {
        ev.preventDefault();
        onNavigate(nextId);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [opened, suspendKeys, prevId, nextId, onNavigate]);

  const dash = t("common:list.dash");
  const payloadText = detail ? JSON.stringify(detail.payload, null, 2) : "";

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={isNarrow ? "100%" : 600}
      withCloseButton={false}
      classNames={{ body: classes.drawerBody, content: classes.drawerContent, header: classes.drawerChromeHeader }}
      title={null}
    >
      {isLoading && !detail && (
        <Stack gap="md" p="lg">
          <Skeleton h={28} w="60%" />
          <Skeleton h={16} w="40%" />
          <Skeleton h={96} />
        </Stack>
      )}

      {error && !detail && (
        <Stack gap="md" p="lg">
          <Alert color="red" radius="md" icon={<WarningIcon size={16} weight="bold" />}>
            {(error as Error).message || t("billing:ledger.drawer.loadError")}
          </Alert>
          <Button variant="default" size="sm" onClick={() => mutate()}>{t("billing:ledger.drawer.retry")}</Button>
        </Stack>
      )}

      {detail && (
        <Box className={classes.drawerInner}>
          <Box className={classes.drawerScroll}>
            <Box className={classes.drawerHeader}>
              <Flex align="center" justify="space-between" gap="sm">
                <Flex align="center" gap="xs" wrap="wrap" miw={0}>
                  <Text span className={classes.drawerEntryNumber}>{eventName(detail.eventType)}</Text>
                  <Text span className={`${classes.pill} ${eventClasses[STATUS_PILL[detail.status]]}`}>
                    {t(`billing:ledger.inbox.status.${detail.status}`)}
                  </Text>
                </Flex>
                <Group gap={4} wrap="nowrap" className={classes.drawerNav}>
                  <Tooltip label={`${t("billing:ledger.inbox.prevEvent")} (←)`} withArrow>
                    <ActionIcon variant="default" size="lg" radius="md" disabled={!prevId} onClick={() => prevId && onNavigate(prevId)} aria-label={t("billing:ledger.inbox.prevEvent")}>
                      <CaretLeftIcon size={16} weight="bold" />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label={`${t("billing:ledger.inbox.nextEvent")} (→)`} withArrow>
                    <ActionIcon variant="default" size="lg" radius="md" disabled={!nextId} onClick={() => nextId && onNavigate(nextId)} aria-label={t("billing:ledger.inbox.nextEvent")}>
                      <CaretRightIcon size={16} weight="bold" />
                    </ActionIcon>
                  </Tooltip>
                  <ActionIcon variant="subtle" color="gray" size="lg" radius="md" onClick={onClose} aria-label={t("common:close")}>
                    <XIcon size={16} weight="bold" />
                  </ActionIcon>
                </Group>
              </Flex>
              <Text className={classes.drawerSubline} mt={6}>
                {[dayjs(detail.occurredAt).format("YYYY-MM-DD HH:mm"), detail.sourceRef].filter(Boolean).join(" · ")}
              </Text>
            </Box>

            <Stack gap="lg" mt="lg">
              <Box component="dl" className={classes.dl}>
                <Box className={classes.dlItem}>
                  <Box component="dt" className={classes.dt}>{t("billing:ledger.inbox.drawerSource")}</Box>
                  <Box component="dd" className={classes.dd}>
                    <Text span size="sm">{aggregateName(detail.aggregateType)}</Text>
                    {detail.sourceRef && <Text span className={classes.code}>{detail.sourceRef}</Text>}
                  </Box>
                </Box>
                <Box className={classes.dlItem}>
                  <Box component="dt" className={classes.dt}>{t("billing:ledger.inbox.colTemplate")}</Box>
                  <Box component="dd" className={classes.dd}>
                    <Tooltip label={detail.templateCode ?? ""} withArrow disabled={!detail.templateCode}>
                      <Text span size="sm">{templateLabel(detail.templateCode) ?? dash}</Text>
                    </Tooltip>
                  </Box>
                </Box>
                <Box className={classes.dlItem}>
                  <Box component="dt" className={classes.dt}>{t("billing:ledger.inbox.receivedAt")}</Box>
                  <Box component="dd" className={classes.dd}>{dayjs(detail.createdAt).format("YYYY-MM-DD HH:mm:ss")}</Box>
                </Box>
                <Box className={classes.dlItem}>
                  <Box component="dt" className={classes.dt}>{t("billing:ledger.inbox.colAttempts")}</Box>
                  <Box component="dd" className={classes.dd}>{detail.retryCount}</Box>
                </Box>
              </Box>

              {detail.status === "PENDING_COST" && (
                <Box className={`${eventClasses.noteBox} ${eventClasses.noteCaution}`}>{t("billing:ledger.inbox.pendingCostNote")}</Box>
              )}
              {detail.status === "SKIPPED" && (
                <Box className={`${eventClasses.noteBox} ${eventClasses.noteNeutral}`}>
                  {t(`billing:ledger.inbox.reason.${detail.lastError ?? "NONE"}`, {
                    defaultValue: detail.lastError || t("billing:ledger.inbox.reason.NONE"),
                  })}
                </Box>
              )}
              {detail.status === "FAILED" && (
                <Box>
                  <Text size="sm" fw={600} mb={6}>{t("billing:ledger.inbox.lastError")}</Text>
                  <Box className={`${eventClasses.noteBox} ${eventClasses.noteError}`} role="alert">
                    {detail.lastError || dash}
                  </Box>
                </Box>
              )}

              {detail.status === "POSTED" && detail.entry && <EntryLinesCard entry={detail.entry} rangeHint={[null, null]} />}

              <Box>
                <Box className={eventClasses.sectionHead}>
                  <Button
                    variant="subtle"
                    color="gray"
                    size="compact-sm"
                    px={0}
                    rightSection={<CaretDownIcon size={12} weight="bold" style={{ transform: payloadOpen ? "rotate(180deg)" : undefined }} />}
                    onClick={() => setPayloadOpen((o) => !o)}
                    aria-expanded={payloadOpen}
                  >
                    {t("billing:ledger.inbox.payload")}
                  </Button>
                  {payloadOpen && (
                    <CopyButton value={payloadText} timeout={1500}>
                      {({ copied, copy }) => (
                        <Tooltip label={copied ? t("billing:ledger.copyDone") : t("billing:ledger.copy")} withArrow>
                          <ActionIcon variant="subtle" color={copied ? "green" : "gray"} size="sm" radius="md" onClick={copy} aria-label={t("billing:ledger.copy")}>
                            {copied ? <CheckIcon size={14} weight="bold" /> : <CopyIcon size={14} />}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                  )}
                </Box>
                <Collapse expanded={payloadOpen}>
                  <Box component="pre" className={eventClasses.payload} mt={6}>{payloadText}</Box>
                </Collapse>
              </Box>
            </Stack>
          </Box>

          <Box className={classes.drawerFooter}>
            <Group gap="sm">
              <Button variant="default" onClick={onClose}>{t("common:close")}</Button>
              {detail.status === "POSTED" && detail.entryId && (
                <Button component={Link} to={`/ledger/journal?entry=${detail.entryId}`} variant="default">
                  {t("billing:ledger.inbox.openJournal")}
                </Button>
              )}
            </Group>
            {canReplay && detail.status !== "POSTED" && (
              <Button variant="outline" leftSection={<ArrowClockwiseIcon size={14} />} onClick={() => onRequestReplay(detail)}>
                {t("billing:ledger.inbox.replay")}
              </Button>
            )}
          </Box>
        </Box>
      )}
    </Drawer>
  );
}
